import React, { useState } from "react";
import clsx from "clsx";

import { Tooltip } from "@material-ui/core";
import TrendingFlatIcon from "@mui/icons-material/TrendingFlat";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SortInactiveIcon from "../../../img/sort-inactive.svg";
import SortAscIcon from "../../../img/sort-asc.svg";
import SortDescIcon from "../../../img/sort-desc.svg";

import { formatToCommaString } from "../../RevampedDashboard/helper/format";
import PercentageBar from "../../Shared/Graph/components/PercentageBar";

import "../styles/KPISummaryRow.scss";
import { toTitleCase } from "../../../utilities/helpers";
import {
  getNextGeoEntityByUserType,
  getSortFieldLabel,
  getSortOrderLabel,
} from "../utils/kpiDashboardHelper";
import { USER_TYPES } from "../../UserList/utils/userConfigs";
import PopperMenu from "../../Shared/PopperMenu/PopperMenu";

const KPISummaryRow = ({
  variant = "data",
  overallAchPct = 0,
  sortField,
  sortDir,
  handleSort,
  row,
  rowIndex,
  focusedRowIndex,
  setFocusedRowIndex,
  onGeoBreakdownView,
  onPerformanceGraphView,
  canViewBreakdown = true,
  isEmployeeDeepDiveOpen = false,
}) => {
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const userType = JSON.parse(localStorage.getItem("currUserType"));
  const menuOptions = [
    {
      label: "View Performance Graph",
      onClick: onPerformanceGraphView,
    },
  ];
  const geoEntity = getNextGeoEntityByUserType(userType);
  if (userType !== USER_TYPES.AGENCY_USER) {
    menuOptions.unshift({
      label: "View Geo Breakdown",
      onClick:
        onGeoBreakdownView && geoEntity
          ? () => {
              onGeoBreakdownView(geoEntity);
              setFocusedRowIndex(null);
            }
          : null,
    });
  }
  const SortIcon = ({ field }) => (
    <img
      src={
        sortField === field && sortDir
          ? sortDir === "asc"
            ? SortAscIcon
            : SortDescIcon
          : SortInactiveIcon
      }
      alt="Sort Icon"
      className="sort-icon"
    />
  );

  const handleMenuClick = (event, targetIndex) => {
    setMenuAnchorEl(event.currentTarget);
    setFocusedRowIndex(targetIndex);
  };

  if (variant === "overall") {
    return (
      <div className="overall-row">
        <div className="cell overall-label">Overall Achievement</div>
        <div className="overall-ach-pct">{overallAchPct.toFixed(2)}%</div>
        <div className="overall-percentage-bar">
          <PercentageBar percentage={overallAchPct} />
        </div>
        <div
          className="overall-menu"
          onClick={(event) => handleMenuClick(event, 0)}
        >
          <MoreVertIcon className="overall-menu-icon" />
          {canViewBreakdown && focusedRowIndex === 0 && (
            <PopperMenu
              anchorElement={menuAnchorEl}
              popperClassName="kpi-parameter-breakdown-popper"
              className="kpi-parameter-breakdown-menu"
              onRequestClose={() => {
                if (!isEmployeeDeepDiveOpen) {
                  setFocusedRowIndex(null);
                }
              }}
              menuItems={menuOptions}
            />
          )}
        </div>
      </div>
    );
  }

  if (variant === "header") {
    const sortFieldLabel = getSortFieldLabel(sortField);
    const sortOrderLabel = getSortOrderLabel(sortDir);
    const showSortInfo = !!sortFieldLabel && !!sortOrderLabel;

    return (
      <div className="header-row">
        <div className="header-cell header-label">Parameters</div>
        <div className="header-cell header-target">Targets</div>
        <div className="header-cell header-ach">Achievements</div>
        <div
          className="header-cell sortable-header-cell header-ach-pct"
          onClick={() => handleSort("ach_pct")}
        >
          <SortIcon field="ach_pct" />
          Ach %
        </div>
        <div
          className="header-cell sortable-header-cell header-growth"
          onClick={() => handleSort("growth_pct")}
        >
          <SortIcon field="growth_pct" />
          Growth %
        </div>
        <div className="header-cell header-bar">Achievement Bar</div>
        <div className={clsx("header-menu", showSortInfo && "sort-info-cell")}>
          {showSortInfo && (
            <span className="sort-info-text">
              {sortFieldLabel} - {sortOrderLabel}
            </span>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="data-row">
      <div className="cell data-label">
        {row.label || toTitleCase(row.code)}
      </div>
      <Tooltip placement="top" title={formatToCommaString(row.target, "en-US")}>
        <div className="cell data-target">
          {formatToCommaString(row.target, "en-US")}
        </div>
      </Tooltip>
      <Tooltip
        placement="top"
        title={formatToCommaString(row?.achievement, "en-US")}
      >
        <div className="cell data-ach">
          {formatToCommaString(row?.achievement, "en-US")}
        </div>
      </Tooltip>
      <div className="cell data-ach-pct sortable-data">
        {row?.ach_pct ? row?.ach_pct.toFixed(2) : 0}%
      </div>
      <div
        className={clsx(
          "data-growth sortable-data",
          (row.growth_pct ?? row.growth ?? 0) > 0
            ? "positive"
            : (row.growth_pct ?? row.growth ?? 0) < 0
              ? "negative"
              : ""
        )}
      >
        {row?.growth_pct === 0 ? (
          <TrendingFlatIcon className="trending-icon" />
        ) : row?.growth_pct < 0 ? (
          <TrendingDownIcon className="trending-icon" />
        ) : (
          <TrendingUpIcon className="trending-icon" />
        )}
        {row?.growth_pct ? row?.growth_pct?.toFixed(2) : 0}%
      </div>
      <div className="data-percentage-bar">
        <PercentageBar percentage={row?.ach_pct || 0} />
      </div>
      <div
        className="data-menu"
        onClick={(event) => handleMenuClick(event, rowIndex)}
      >
        <MoreVertIcon className="data-menu-icon" />
        {canViewBreakdown && focusedRowIndex === rowIndex && (
          <PopperMenu
            anchorElement={menuAnchorEl}
            popperClassName="kpi-parameter-breakdown-popper"
            className="kpi-parameter-breakdown-menu"
            onRequestClose={() => {
              if (!isEmployeeDeepDiveOpen) {
                setFocusedRowIndex(null);
              }
            }}
            menuItems={menuOptions}
          />
        )}
      </div>
    </div>
  );
};

export default KPISummaryRow;
